/**
 * ConfluenceCategoriesAccordion
 *
 * Replaces the flat wall of ~100 confluence chips across ~12 categories
 * in AddEditTradeModal with a searchable, collapsible accordion.
 *
 * Behavior:
 *   - Each category is a collapsible section with a header showing the
 *     selected-chip count (e.g. "Price Action (2)").
 *   - SMART AUTO-EXPAND on mount: any category that has at least one
 *     selected chip starts expanded. Empty categories stay collapsed.
 *     The trader instantly sees what they've tagged without scrolling.
 *   - SEARCH FILTER at the top of the section: filters chips live
 *     across all categories. When the filter has text, categories with
 *     ≥1 matching chip auto-expand (overriding manual collapse) and
 *     categories with zero matches hide entirely.
 *   - Click a chip to toggle. The toggle handler is passed in so the
 *     same component drives both the "For" and "Against" sides — only
 *     the styling + opposite-side disabling logic differs per variant.
 *
 * The component handles BOTH the For-confluences section and the
 * Against-confluences (counter) section. Distinguished by `variant`.
 *
 * Props
 * -----
 * confluences        — full library array of { id, name, category }
 * selectedIds        — ids the user has selected on THIS side
 * oppositeSelectedIds — ids selected on the OPPOSITE side; those chips
 *                      are disabled (greyed-out) here so the user can't
 *                      have the same confluence on both For and Against
 *                      simultaneously.
 * recommendedIds     — (optional) setup-recommended chip ids. Visible
 *                      only on the For side. When set, recommended chips
 *                      render with a star + purple outline so the user
 *                      can see what their playbook expects.
 * onToggle           — (id) => void; called on chip click.
 * darkMode           — bool.
 * selectedSetupName  — (optional) used in chip tooltip ("Recommended for X").
 * variant            — 'for' | 'counter'. Drives selected-chip color
 *                      (purple for For, orange for Against) and the
 *                      "blocked by opposite side" tooltip wording.
 */

import { useMemo, useState } from 'react';
import { ChevronDown, Search } from 'lucide-react';
import { getConfluencesByCategory } from '../../../utils/constants';

const ConfluenceCategoriesAccordion = ({
  confluences = [],
  selectedIds = [],
  oppositeSelectedIds = [],
  recommendedIds = [],
  onToggle,
  darkMode = true,
  selectedSetupName = '',
  variant = 'for',
}) => {
  const isCounter = variant === 'counter';

  // Group the library by category once per `confluences` change.
  const categories = useMemo(
    () => getConfluencesByCategory(confluences),
    [confluences]
  );

  // ── Search / filter ──────────────────────────────────────────────
  const [filterText, setFilterText] = useState('');
  const normalizedFilter = filterText.trim().toLowerCase();

  // Apply the live filter. Empty filter → return everything; otherwise
  // keep only chips whose name contains the filter substring (case-
  // insensitive). Categories with zero matches drop out entirely.
  const filteredCategories = useMemo(() => {
    if (!normalizedFilter) return categories;
    const out = {};
    for (const [cat, items] of Object.entries(categories)) {
      const matches = items.filter(c =>
        String(c.name || '').toLowerCase().includes(normalizedFilter)
      );
      if (matches.length > 0) out[cat] = matches;
    }
    return out;
  }, [categories, normalizedFilter]);

  // ── Expand state ─────────────────────────────────────────────────
  // Initialized lazily from selectedIds — every category that already
  // has at least one selected chip starts expanded. Subsequent manual
  // toggles override per-category. A new selection auto-expands the
  // containing category on the next render via the `derivedExpanded`
  // memo below, so the user always sees the chip they just tagged.
  const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const [manualExpand, setManualExpand] = useState(() => {
    const init = {};
    for (const [cat, items] of Object.entries(categories)) {
      if (items.some(c => selectedIdSet.has(c.id))) init[cat] = true;
    }
    return init;
  });

  // Final expanded state per category. Priority:
  //   1. Active filter → force-expand any category that has filter matches
  //      (so the user can see the chips they're searching for)
  //   2. Manual toggle state (user explicitly opened/closed it)
  //   3. Smart default — categories with current selections stay open
  const isCategoryExpanded = (cat) => {
    if (normalizedFilter) return true;
    if (cat in manualExpand) return manualExpand[cat];
    // Smart default: re-check selectedIdSet on each render so newly-
    // tagged chips reveal their category even if it was previously empty.
    const items = categories[cat] || [];
    return items.some(c => selectedIdSet.has(c.id));
  };

  const toggleCategory = (cat) => {
    setManualExpand(prev => ({ ...prev, [cat]: !isCategoryExpanded(cat) }));
  };

  // ── Per-category aggregates for the headers ──────────────────────
  const headerStatsFor = (categoryItems) => {
    let selected = 0;
    let recommended = 0;
    for (const c of categoryItems) {
      if (selectedIdSet.has(c.id)) selected++;
      if (!isCounter && recommendedIds.includes(c.id)) recommended++;
    }
    return { selected, recommended, total: categoryItems.length };
  };

  // ── Chip rendering ───────────────────────────────────────────────
  const renderChip = (conf) => {
    const isSelected = selectedIdSet.has(conf.id);
    const isInOpposite = oppositeSelectedIds.includes(conf.id);
    const isRecommended = !isCounter && recommendedIds.includes(conf.id);

    // Disabled when opposite side has it. The handler short-circuits
    // anyway, but the cursor + opacity tell the user why.
    const oppositeTip = isCounter ? 'Used as For confluence' : 'Used as Against confluence';
    const recommendedTip = isRecommended && selectedSetupName
      ? `Recommended for ${selectedSetupName}`
      : '';

    let chipClass;
    if (isInOpposite) {
      chipClass = darkMode
        ? 'bg-gray-800 text-gray-600 cursor-not-allowed opacity-40'
        : 'bg-gray-100 text-gray-400 cursor-not-allowed opacity-40';
    } else if (isSelected) {
      chipClass = isCounter ? 'bg-orange-500 text-white' : 'bg-purple-500 text-white';
    } else if (isRecommended) {
      chipClass = darkMode
        ? 'bg-purple-900/40 text-purple-300 border border-purple-500/50 hover:bg-purple-800/50'
        : 'bg-purple-100 text-purple-700 border border-purple-300 hover:bg-purple-200';
    } else {
      chipClass = darkMode
        ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
        : 'bg-gray-200 text-gray-700 hover:bg-gray-300';
    }

    return (
      <button
        key={conf.id}
        type="button"
        onClick={() => !isInOpposite && onToggle?.(conf.id)}
        className={`px-3 py-1 rounded-full text-sm transition-colors duration-300 relative ${chipClass}`}
        title={isInOpposite ? oppositeTip : recommendedTip}
      >
        {isRecommended && !isSelected && !isInOpposite && <span className="mr-1">★</span>}
        {conf.name}
      </button>
    );
  };

  // ── Empty state ──────────────────────────────────────────────────
  if (Object.keys(categories).length === 0) return null;

  const filteredEntries = Object.entries(filteredCategories);
  const totalSelected = selectedIds.length;
  const noFilterMatches = normalizedFilter && filteredEntries.length === 0;

  // ── Render ───────────────────────────────────────────────────────
  return (
    <div>
      {/* Search input + persistent "X selected" badge + clear button.
          The badge stays visible even when the user is typing into the
          input — previously it lived in the placeholder and disappeared
          on the first keystroke. Layout from left to right:
            [🔍] [   input   ] [N selected]? [✕]?  */}
      <div className="relative mb-3">
        <Search
          size={14}
          className={`absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none ${
            darkMode ? 'text-gray-500' : 'text-gray-400'
          }`}
        />
        {/* Pad-right grows to accommodate the badge + clear button so the
            input text doesn't overlap them. ~110px is enough for "12 selected ✕". */}
        <input
          type="text"
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          placeholder={`Filter ${Object.keys(categories).length} categories…`}
          className={`w-full input-modern pl-8 ${totalSelected > 0 || filterText ? 'pr-32' : 'pr-3'}`}
        />
        {/* Right-side cluster: selection badge + clear-filter button.
            Both are absolutely positioned so the input can keep its full
            width and the badge stays anchored regardless of input value. */}
        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1.5 pointer-events-none">
          {totalSelected > 0 && (
            <span
              className={`text-[11px] font-medium px-1.5 py-0.5 rounded-full ${
                isCounter
                  ? (darkMode ? 'bg-orange-500/20 text-orange-300' : 'bg-orange-100 text-orange-700')
                  : (darkMode ? 'bg-purple-500/20 text-purple-300' : 'bg-purple-100 text-purple-700')
              }`}
              title={`${totalSelected} ${isCounter ? 'against' : 'for'} confluence${totalSelected === 1 ? '' : 's'} selected`}
            >
              {totalSelected} selected
            </span>
          )}
          {filterText && (
            <button
              type="button"
              onClick={() => setFilterText('')}
              className={`pointer-events-auto text-xs px-1.5 py-0.5 rounded ${
                darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'
              }`}
              title="Clear filter"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* "No matches" hint when filter rules out every chip. */}
      {noFilterMatches && (
        <div className={`text-center text-xs italic py-4 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
          No confluences match &ldquo;{filterText}&rdquo;
        </div>
      )}

      {/* Category accordion list. */}
      <div className="space-y-2">
        {filteredEntries.map(([category, categoryConfluences]) => {
          const { selected, recommended } = headerStatsFor(
            // For the header counts we want the UN-filtered totals so the
            // count doesn't lie about how many chips a category has when
            // the user has a filter active. The chip list below still uses
            // the filtered items.
            categories[category] || []
          );
          const expanded = isCategoryExpanded(category);

          return (
            <div
              key={category}
              className={`rounded-md border transition-colors ${
                darkMode
                  ? 'border-white/5 bg-white/[0.02]'
                  : 'border-gray-200 bg-gray-50/60'
              }`}
            >
              {/* Header — click to toggle. Title + chevron + badges
                  centered as a group to match the modal's overall
                  center-aligned aesthetic (Event Tags, Mistakes, and the
                  parent "Confluences / Market Conditions" heading are all
                  centered). The whole row stays clickable. */}
              <button
                type="button"
                onClick={() => toggleCategory(category)}
                className={`w-full flex items-center justify-center px-3 py-2 rounded-md transition-colors ${
                  darkMode ? 'hover:bg-white/[0.04]' : 'hover:bg-gray-100/80'
                }`}
              >
                <span className="flex items-center gap-2">
                  <ChevronDown
                    size={14}
                    className={`transition-transform duration-200 ${
                      expanded ? 'rotate-0' : '-rotate-90'
                    } ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}
                  />
                  <span className={`text-base font-semibold ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                    {category}
                  </span>
                  {selected > 0 && (
                    <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${
                      isCounter
                        ? (darkMode ? 'bg-orange-500/20 text-orange-300' : 'bg-orange-100 text-orange-700')
                        : (darkMode ? 'bg-purple-500/20 text-purple-300' : 'bg-purple-100 text-purple-700')
                    }`}>
                      {selected} selected
                    </span>
                  )}
                  {!isCounter && recommended > 0 && (
                    <span
                      className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                        darkMode ? 'bg-purple-900/40 text-purple-300 border border-purple-500/30' : 'bg-purple-50 text-purple-700 border border-purple-200'
                      }`}
                      title={selectedSetupName ? `Recommended for ${selectedSetupName}` : ''}
                    >
                      ★ {recommended}
                    </span>
                  )}
                </span>
              </button>

              {/* Body — chips, collapsible. Chips center-justified to
                  match the Event Tags + Mistakes sections above (and the
                  original pre-accordion confluence layout). Header stays
                  full-width-clickable with chevron on the left — that's
                  standard accordion convention and shouldn't be centered. */}
              {expanded && (
                <div className="flex flex-wrap justify-center gap-2 px-3 pb-3 pt-1">
                  {categoryConfluences.map(renderChip)}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ConfluenceCategoriesAccordion;
